import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import selectDownArrow from "../../../../assets/images/selectDownArrow.svg"; // Adjust this path as needed

const SharedCountryDropdown = ({
  label = "City",
  placeholder = "Select City",
  selectedCity,
  setSelectedCity,
  onCountryChange = () => {},
  disabled = false,
  countryCode = "",
  countryName = "",
}) => {
  const [cityOptions, setCityOptions] = useState([]);
  const [citySearch, setCitySearch] = useState("");
  // Kept separate from `citySearch` so opening the dropdown always shows the
  // full list even when a city is already selected in the input.
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
      
  // Fetch cities using countryName (preferred) or fall back to countryCode
  const fetchCountries = React.useCallback(async () => {
    const nameToQuery = countryName || countryCode;
    if (!nameToQuery) return;

    try {
      const response = await axios.post(
        'https://countriesnow.space/api/v0.1/countries/cities',
        { country: nameToQuery }
      );

      const cities = response.data?.data;
      if (Array.isArray(cities)) {
        console.log(`Found ${cities.length} cities in ${nameToQuery}`);
        const sortedCities = cities.map((cityName) => ({
          label: cityName,
          value: cityName,
          country: countryCode,
        }));
        setCityOptions(sortedCities);
      } else {
        console.error('No cities available for:', nameToQuery);
        setCityOptions([]);
      }
    } catch (error) {
      console.error("Failed to fetch cities:", error);
      setCityOptions([]);
    }
  }, [countryCode, countryName]);

  useEffect(() => {
    if (countryName || countryCode) {
      fetchCountries();
    } else {
      setCityOptions([]);
      setSelectedCity("");
      setCitySearch("");
      onCountryChange({ name: "", country: "" });
      setDropdownOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode, countryName, fetchCountries]);

  // Handle country selection
  const handleCitySelect = (selectedName) => {
    const selected = cityOptions.find((c) => c.value === selectedName);
    // const currencyName = selected?.currency?.name || "";
    // const currencySymbol = selected?.currency?.symbol || "";

    setSelectedCity(selectedName);
    onCountryChange({
      name: selectedName,
      country: selected?.country,
      //   currency: currencyName ? `${currencyName} (${currencySymbol})` : "",
      //   currencySymbol,
      //   countryCode: selected?.code || "",
    });

    setCitySearch("");
    setSearchQuery("");
    setDropdownOpen(false);
  };

  const toggleDropdown = () => {
    if (dropdownOpen) {
      setDropdownOpen(false);
    } else {
      setSearchQuery("");
      setDropdownOpen(true);
    }
  };

  const openDropdown = () => {
    setSearchQuery("");
    setDropdownOpen(true);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setCitySearch(selectedCity || "");
  }, [selectedCity]);

  return (
    <Form.Group className="custom-form">
      {label && <Form.Label className="color-head">{label}</Form.Label>}

      <div
        className={`country-dropdown-wrapper ${disabled ? "disabled" : ""}`}
        ref={dropdownRef}
        aria-disabled={disabled}
      >
        <InputGroup
          className="country-search-box"
          onClick={() => {
            if (!disabled) toggleDropdown();
          }}
        >
          <Form.Control
            type="text"
            className="country-input"
            placeholder={placeholder}
            value={citySearch}
            onChange={(e) => {
              const value = e.target.value;
              setCitySearch(value);
              setSearchQuery(value);
              if (!disabled) setDropdownOpen(true);

              if (cityOptions?.length === 0) {
                setSelectedCity(value);
                onCountryChange({
                  name: value,
                  country: countryCode,
                });
              }

              // NEW: Clear selectedCity & formData.region when emptied
              if (value.trim() === "") {
                setSelectedCity("");
                onCountryChange({
                  name: "",
                  country: "",
                  //   currency: "",
                  //   currencySymbol: "",
                  //   countryCode: "",
                });
              }
            }}
            // onFocus={() => {
            //   if (!disabled) openDropdown();
            // }}
            disabled={disabled}
          />
          <InputGroup.Text
            className="dropdown-arrow-wrapper"
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) openDropdown();
            }}
            style={{
              backgroundColor: "transparent",
              border: "none",
            }}
          >
            <img
              src={selectDownArrow}
              alt="CaretDown"
              className="dropdown-arrow"
            />
          </InputGroup.Text>
        </InputGroup>

        {dropdownOpen && (
          <div className="country-dropdown-list" role="listbox" aria-disabled={disabled}>
            {cityOptions
              .filter((item) =>
                item.label.toLowerCase().includes(searchQuery.toLowerCase().trim())
              )
              .map((item, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                    if (!disabled) handleCitySelect(item.value);
                  }}
                  className={`country-option ${disabled ? "disabled" : ""}`}
                  role="option"
                  aria-selected={selectedCity === item.value}
                  tabIndex={disabled ? -1 : 0}
                >
                  {item.label}
                </div>
              ))}
          </div>
        )}
      </div>
    </Form.Group>
  );
};

export default SharedCountryDropdown;
